import '../style/global.css';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import React from "react";
import { useHistory, useLocation } from 'react-router-dom';
import { ActivityEnum, AdminSubactivityEnum, useUserActivityTracker } from '../UserActivityTracker';
import { UserAdminApi } from '../api/UserAdminApi';
import { IUser } from '../api/UserApi';
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from '../context/AppBarContext';


export default function UserAdmin() {

    useUserActivityTracker(ActivityEnum.Admin, AdminSubactivityEnum.UserAdmin)

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const [searchFirstName, setSearchFirstName] = React.useState<string | undefined>(undefined);
    const [searchLastName, setSearchLastName] = React.useState<string | undefined>(undefined);
    const [searchEmail, setSearchEmail] = React.useState<string | undefined>(undefined);
    const [searchUserList, setSearchUserList] = React.useState<IUser[]>([]);

    const [hasSearched, setHasSearched] = React.useState(false);

    // change password state (tracked per expanded row)
    const [newPassword, setNewPassword] = React.useState<string>('');
    const [isPasswordSaving, setIsPasswordSaving] = React.useState<boolean>(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState<string>('');

    // delete dialog state
    const [isDeleting, setIsDeleting] = React.useState<boolean>(false);

    // Popup for row actions (replaces inline collapse)
    const [actionsOpen, setActionsOpen] = React.useState(false);
    const [actionUserId, setActionUserId] = React.useState<number | null>(null);

    // DELETE confirmation text
    const [deleteConfirm, setDeleteConfirm] = React.useState<string>('');

    // optional: make building the summary easy
    const filterSummary = React.useMemo(() => {
        const parts: string[] = [];
        if (searchFirstName) parts.push(`first name "${searchFirstName}"`);
        if (searchLastName) parts.push(`last name "${searchLastName}"`);
        if (searchEmail) parts.push(`email "${searchEmail}"`);
        return parts.join(' • ') || 'your criteria';
    }, [searchFirstName, searchLastName, searchEmail]);

    const actionUser = React.useMemo(
        () => searchUserList.find((u) => u.id === actionUserId) || null,
        [searchUserList, actionUserId]
    );

    const handleSearchFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchFirstName(e.target.value);
    };

    const handleSearchLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchLastName(e.target.value);
    };

    const handleSearchEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchEmail(e.target.value);
    };

    // Run search (reusable)
    const search = React.useCallback(() => {
        setHasSearched(true);
        UserAdminApi.search(searchFirstName || undefined, searchLastName || undefined, searchEmail || undefined)
            .then((response) => {
                if (response.status < 300) return response.data;
                console.error(response);
                handleErrorMessage(response, setErrorMessage, history, location);
                return [];
            })
            .then((userList) => setSearchUserList(userList))
            .catch((err) => handleErrorMessage(err, setErrorMessage, history, location));
    }, [searchFirstName, searchLastName, searchEmail, setErrorMessage, history, location]);

    // Form submit calls doSearch
    const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        search();
    };

    // Optional: pressing Enter in any field triggers search immediately
    const handleFieldKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            search();
        } else {
            setHasSearched(false)
        }
    };


    const handleRowClick = (userId: number) => {
        openActionsDialog(userId);
    };

    const handleChangePassword = () => {
        if (!actionUserId) return;
        if (!newPassword) { setPasswordErrorMessage('Enter a new password first.'); return; }
        setIsPasswordSaving(true);
        setPasswordErrorMessage('');
        UserAdminApi.changePassword(actionUserId, newPassword)
            .then((response) => {
                if (response.status < 300) {
                    setPasswordErrorMessage('Password changed.');
                } else {
                    handleErrorMessage(response, setErrorMessage, history, location);
                }
            })
            .catch((err) => handleErrorMessage(err, setErrorMessage, history, location))
            .finally(() => setIsPasswordSaving(false));
    };

    const handleConfirmDelete = () => {
        if (!actionUserId) return;
        if (deleteConfirm !== 'DELETE') return; // guard
        setIsDeleting(true);
        UserAdminApi.delete(actionUserId)
            .then((response) => {
                if (response.status < 300) {
                    setSearchUserList((prev) => prev.filter((u: any) => u.id !== actionUserId));
                    setActionsOpen(false);
                    setActionUserId(null);
                } else {
                    handleErrorMessage(response, setErrorMessage, history, location);
                }
            })
            .catch((err) => handleErrorMessage(err, setErrorMessage, history, location))
            .finally(() => setIsDeleting(false));
    };

    const openActionsDialog = (userId: number) => {
        setActionUserId(userId);
        setNewPassword('');
        setPasswordErrorMessage('');
        setDeleteConfirm('');
        setActionsOpen(true);
    };

    const closeActionsDialog = () => {
        if (!isDeleting && !isPasswordSaving) {
            setActionsOpen(false);
            setActionUserId(null);
            setDeleteConfirm('');
        }
    };

    return (<div>

        <form onSubmit={handleSearchSubmit}>
            <Stack direction="row" spacing={2} sx={{ width: '90%', margin: '0 auto', pt: 2, pb: 2 }}>
                <TextField
                    label="First Name"
                    value={searchFirstName}
                    onChange={handleSearchFirstNameChange}
                    onKeyDown={handleFieldKeyDown}
                />
                <TextField
                    label="Last Name"
                    value={searchLastName}
                    onChange={handleSearchLastNameChange}
                    onKeyDown={handleFieldKeyDown}
                />
                <TextField
                    label="Email"
                    value={searchEmail}
                    onChange={handleSearchEmailChange}
                    onKeyDown={handleFieldKeyDown}
                />
                <Button type="submit" variant="contained">
                    Search
                </Button>
            </Stack>
        </form>

        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell width={56} />
                        <TableCell>First Name</TableCell>
                        <TableCell>Last Name</TableCell>
                        <TableCell>Email</TableCell>
                    </TableRow>
                </TableHead>
                {hasSearched && searchUserList.length === 0 ? (
                    <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                            {`No results for ${filterSummary}.`}
                        </TableCell>
                    </TableRow>
                ) : (
                    <TableBody>
                        {searchUserList.map((user) => {
                            const userId = user.id ? user.id : -1;
                            return (
                                <React.Fragment key={userId}>
                                    <TableRow
                                        hover
                                        onClick={() => handleRowClick(userId)}
                                        sx={{ cursor: 'pointer', '&:hover': { filter: 'brightness(0.98)' } }}
                                    >
                                        <TableCell width={56}>
                                            <IconButton
                                                size="small"
                                                aria-label="open actions"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openActionsDialog(userId);
                                                }}
                                            >
                                                <ExpandMoreIcon />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{(user as any).first_name}</TableCell>
                                        <TableCell>{(user as any).last_name}</TableCell>
                                        <TableCell>{(user as any).email}</TableCell>
                                    </TableRow>
                                </React.Fragment>);
                        })}
                    </TableBody>
                )}
            </Table>
        </TableContainer>

        <Dialog
            open={actionsOpen}
            onClose={closeActionsDialog}
            fullWidth
            maxWidth="sm"
            aria-labelledby="user-actions-title"
        >
            <DialogTitle id="user-actions-title">
                {actionUser ? `${actionUser.first_name} ${actionUser.last_name}` : 'User actions'}
                {(actionUser && actionUser.email) ? ` — ${actionUser.email}` : ''}
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={3}>
                    {/* Change password */}
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>Change password</Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                            <TextField
                                type="password"
                                label="New password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                size="small"
                                sx={{ maxWidth: 320 }}
                            />
                            <Button
                                variant="contained"
                                disabled={isPasswordSaving || !newPassword}
                                onClick={handleChangePassword}
                            >
                                {isPasswordSaving ? 'Changing…' : 'Change password'}
                            </Button>
                        </Stack>
                        {passwordErrorMessage && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {passwordErrorMessage}
                            </Typography>
                        )}
                    </Box>

                    {/* Danger zone: type DELETE to enable */}
                    <Box>
                        <Typography variant="subtitle2" color="error" gutterBottom>Danger zone</Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Type <strong>DELETE</strong> to enable the delete button.
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                            <TextField
                                label='Type "DELETE" to confirm'
                                value={deleteConfirm}
                                onChange={(e) => setDeleteConfirm(e.target.value)}
                                size="small"
                                inputProps={{ 'aria-label': 'delete confirmation' }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && deleteConfirm === 'DELETE' && !isDeleting) {
                                        e.preventDefault();
                                        handleConfirmDelete();
                                    }
                                }}
                            />
                            <Button
                                variant="outlined"
                                color="error"
                                disabled={deleteConfirm !== 'DELETE' || isDeleting}
                                onClick={handleConfirmDelete}
                            >
                                {isDeleting ? 'Deleting…' : 'Delete user'}
                            </Button>
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={closeActionsDialog} disabled={isDeleting || isPasswordSaving}>Close</Button>
            </DialogActions>
        </Dialog>

    </div>);
}